import { type Unsubscribe } from '../utils/Unsubscribe';

/**
 * Schedules one callback per display refresh.
 *
 * An interface rather than a direct `requestAnimationFrame` call so the loop can be driven frame by
 * frame in tests, with no browser and no fake timers.
 */
export interface FrameScheduler {
  /** Requests one callback, passing a monotonically increasing timestamp in milliseconds. */
  request(callback: (timestampMs: number) => void): number;
  /** Cancels a pending request. Canceling an already fired handle does nothing. */
  cancel(handle: number): void;
}

/**
 * Reports whether the page is currently on screen.
 *
 * Separate from {@link FrameScheduler} because they answer different questions, and a test usually
 * wants to fake one without the other.
 */
export interface VisibilitySource {
  /** Whether the page is currently visible. */
  readonly isVisible: boolean;
  /** Subscribes to visibility changes and returns the function that cancels the subscription. */
  onVisibilityChange(listener: () => void): Unsubscribe;
}

/** The browser's own frame scheduler. */
export function createBrowserFrameScheduler(): FrameScheduler {
  return {
    request: (callback) => requestAnimationFrame(callback),
    cancel: (handle) => {
      cancelAnimationFrame(handle);
    },
  };
}

/** Visibility as reported by the document. */
export function createDocumentVisibilitySource(): VisibilitySource {
  return {
    get isVisible(): boolean {
      return document.visibilityState === 'visible';
    },
    onVisibilityChange: (listener) => {
      document.addEventListener('visibilitychange', listener);
      return () => {
        document.removeEventListener('visibilitychange', listener);
      };
    },
  };
}
